import { AnimatePresence } from 'framer-motion';
import { FC, useState } from 'react';
import styled from 'styled-components';

import { ReactComponent as ImageIcon } from '@/assets/icons/img-placeholder.svg';
import { ReactComponent as VideoIcon } from '@/assets/icons/video-placeholder.svg';
import VideoPreviewImg from '@/assets/icons/video-preview.svg';
import Text from '@/components/Text';
import Form from '@/pages/MLProjects/components/Form';
import { MLProject } from '@/types/resources';
import { MLProjectStatus } from '@/utils/enums';
import { humanFileSize } from '@/utils/helpers';

import Card, { ActionButton } from './Card';

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 22px;
`;

const FileBox = styled.div`
  display: flex;
  align-items: center;
  justify-items: center;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  padding: 16px;
  a {
    line-height: 0;
  }
`;

const FileDescription = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  margin-left: 24px;
`;

const FilePreview = styled.img.attrs({ alt: '' })`
  height: 88px;
  width: auto;
`;

export const Label = styled(Text).attrs({
  $type: 'smallHeadline',
  $color: 'gray',
})`
  margin-bottom: 8px;
`;

export const Details = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
`;

export const DetailsBlock = styled.div`
  padding: 12px 0;
  &:nth-child(even) {
    padding-left: 24px;
    border-left: 1px solid ${({ theme }) => theme.colors.lightGray};
  }
`;

const NoFilePlaceholder = styled(Text).attrs({
  $type: 'smallBold',
  $color: 'gray',
})`
  display: flex;
  flex: 1;
  padding: 16px 0;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

interface Props {
  project: MLProject;
}

const MainInfoCard: FC<Props> = ({ project }) => {
  const [showEditForm, setShowEditForm] = useState(false);

  const isProjectEditable =
    project.status === MLProjectStatus.DRAFT && !project.translations?.length;

  return (
    <>
      <Card
        storageKey={`ml_project_${project.id}_main_info_visibility`}
        title="Main Info"
        defaultIsOpened={isProjectEditable}
        description="Necessary information to facilitate the distribution of this project by the end"
        actions={
          <ActionButton
            onClick={() => setShowEditForm(true)}
            disabled={!isProjectEditable}
          >
            Edit
          </ActionButton>
        }
      >
        <Rows>
          <div>
            <Label>Video</Label>
            <FileBox>
              {project.video ? (
                <>
                  <a
                    target="_blank"
                    href={project.video.versions.original}
                    rel="noreferrer"
                  >
                    <FilePreview src={VideoPreviewImg} />
                  </a>
                  <FileDescription>
                    <Text $type="smallNormal">{project.video.name}</Text>
                    <Text $type="smallNormal" $color="gray">
                      {humanFileSize(project.video.size, true)}
                    </Text>
                  </FileDescription>
                </>
              ) : (
                <NoFilePlaceholder>
                  <VideoIcon />
                  <span>No video added</span>
                </NoFilePlaceholder>
              )}
            </FileBox>
          </div>
          <div>
            <Label>Thumbnail</Label>
            <FileBox>
              {project.thumbnail ? (
                <>
                  <a
                    target="_blank"
                    href={project.thumbnail.versions.original}
                    rel="noreferrer"
                  >
                    <FilePreview src={project.thumbnail.versions.original} />
                  </a>
                  <FileDescription>
                    <Text $type="smallNormal">{project.thumbnail.name}</Text>
                    <Text $type="smallNormal" $color="gray">
                      {humanFileSize(project.thumbnail.size, true)}
                    </Text>
                  </FileDescription>
                </>
              ) : (
                <NoFilePlaceholder>
                  <ImageIcon />
                  <span>No thumbnail added</span>
                </NoFilePlaceholder>
              )}
            </FileBox>
          </div>
          <Details>
            <DetailsBlock>
              <Label>Title</Label>
              <Text $type="captionSemibold">{project.title}</Text>
            </DetailsBlock>
            <DetailsBlock>
              <Label>Description</Label>
              <Text $type="captionSemibold">{project.description ?? '-'}</Text>
            </DetailsBlock>
            <DetailsBlock>
              <Label>Project ID</Label>
              <Text $type="captionSemibold">{project.projectId}</Text>
            </DetailsBlock>
            <DetailsBlock>
              <Label>Fresh ID</Label>
              <Text $type="captionSemibold">{project.freshId}</Text>
            </DetailsBlock>
            <DetailsBlock>
              <Label>Production Team</Label>
              <Text $type="captionSemibold">{project.productionTeam}</Text>
            </DetailsBlock>
          </Details>
        </Rows>
      </Card>
      <AnimatePresence>
        {showEditForm && (
          <Form
            onClose={() => setShowEditForm(false)}
            editableProject={project}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default MainInfoCard;
